import { useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  GenericLink,
  useBreakpoint,
  Breakpoints,
  ButtonWidth,
  NextgenImage,
  Theme,
  LoaderType,
  type ButtonTheme,
} from '@nextgen-web-framework/all';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import { getCustomThemeColorClass, ThemeColorClassTypes, ThemeColor, ThemeShadesType } from '../../utils/themeColor';
import styles from './FeaturedArticle.module.scss';
import { ThemeMapper, type FeaturedArticleProps } from './types/propsTypes';

const FeaturedArticle = (featuredArticleProps: FeaturedArticleProps) => {
  const viewPortType = useBreakpoint();
  const [imgLoading, setImgLoading] = useState(true);
  const isMobile = [Breakpoints.SM, Breakpoints.XS].includes(viewPortType as Breakpoints);
  const cardImage = featuredArticleProps.images?.find((item) => item.viewport.includes(viewPortType));

  //get theme color from props
  const themeColor = featuredArticleProps.themeColor
    ? getCustomThemeColorClass(
        ThemeColor[featuredArticleProps.themeColor],
        featuredArticleProps.themeShade as ThemeShadesType,
      )
    : undefined;

  let theme: string;
  let themeButton: ButtonTheme;

  if (themeColor) {
    theme = themeColor[ThemeColorClassTypes.Base];
    themeButton = themeColor[ThemeColorClassTypes.Button] as ButtonTheme;
  } else {
    theme = featuredArticleProps.cardTheme ?? ('theme-black' as keyof typeof ThemeMapper);
    themeButton = Theme.BLACK;
  }

  return (
    <div
      className={`${styles['featured-article-card-container']} ${styles[theme]}`}
      data-testid="featured-article-card-testid"
    >
      <div className={styles['featured-article-image-container']}>
        {cardImage?.url ? (
          <>
            <NextgenImage
              src={cardImage?.url ?? ''}
              className={styles['featured-article-image']}
              alt={cardImage?.altText ?? ''}
              onLoad={() => setImgLoading(false)}
              loaderType={LoaderType.CONTENTSTACK}
            />
            {imgLoading && <SkeletonLoader className={styles['featured-article-image']} isRoundCorner={false} />}
          </>
        ) : (
          <SkeletonLoader className={styles['featured-article-image']} isRoundCorner={false} />
        )}
      </div>
      <div className={`${styles['featured-article-content-container']}`}>
        <div className={`${styles['featured-article-wrapper']}`}>
          <div className={styles['featured-article-wrapper-header']}>
            {featuredArticleProps.brandTitle && (
              <h6 className={styles['featured-article-title']}>{featuredArticleProps.brandTitle}</h6>
            )}
            <h2 className={styles['featured-article-headline']}>{featuredArticleProps.brandHeadLine}</h2>
          </div>
          <div className={styles['featured-article-description']}>{featuredArticleProps.brandDescription}</div>
          {featuredArticleProps.brandReadTime && (
            <div className={styles['featured-article-read-time']}>{featuredArticleProps.brandReadTime}</div>
          )}
        </div>
        {!!featuredArticleProps.learnMore.url && (
          <div className={styles['featured-article-footer-button']}>
            <GenericLink
              href={featuredArticleProps.learnMore.url}
              forceNative={false}
              target={featuredArticleProps.learnMore.target}
            >
              <Button
                title={featuredArticleProps.learnMore.text}
                buttonStyle={ButtonStyles.SOLID}
                theme={themeButton}
                size={ButtonSize.MEDIUM}
                buttonWidth={isMobile ? ButtonWidth.FULL_WIDTH : ButtonWidth.FIT_CONTENT}
                data-testid="featured-article-button-testid"
              />
            </GenericLink>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedArticle;
